// Radial cards slider — seamless continuous radial marquee for all projects
import { gsap, ScrollTrigger } from '../gsap-config.js';
import { initAnimationsForElement } from '../utils/animations.js';

const DEFAULT_LOOP_DURATION = 34;

function debounceOnWidthChange(fn, ms) {
  let lastWidth = window.innerWidth;
  let timer;

  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      fn(...args);
    }, ms);
  };
}

function getNumberAttr(el, name, fallback) {
  const value = parseFloat(el.getAttribute(name));
  return Number.isFinite(value) ? value : fallback;
}

function cleanupContainer(container) {
  if (container._radialSliderAbort) {
    container._radialSliderAbort.abort();
    container._radialSliderAbort = null;
  }

  if (container._radialSliderMarquee) {
    container._radialSliderMarquee.kill();
    container._radialSliderMarquee = null;
  }

  if (container._radialSliderScrollTrigger) {
    container._radialSliderScrollTrigger.kill();
    container._radialSliderScrollTrigger = null;
  }

  if (container._radialSliderState) {
    gsap.killTweensOf(container._radialSliderState);
    container._radialSliderState = null;
  }

  container.querySelectorAll('[data-radial-slider-clone]').forEach((el) => el.remove());
}

export function initRadialCardsSlider() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-radial-slider-init]').forEach((container) => {
    cleanupContainer(container);

    const collection = container.querySelector('[data-radial-slider-collection]');
    const track = container.querySelector('[data-radial-slider-list]');
    if (!collection || !track) return;

    const originalItems = Array.from(
      container.querySelectorAll('[data-radial-slider-item]:not([data-radial-slider-clone])')
    );
    if (!originalItems.length) return;

    const carouselLabel = container.getAttribute('aria-label') || 'Related projects';
    container.setAttribute('role', 'region');
    container.setAttribute('aria-roledescription', 'marquee');
    container.setAttribute('aria-label', carouselLabel);

    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', 'Slides');

    const dotsWrap = container.querySelector('[data-radial-slider-generate-dots]');
    if (dotsWrap) {
      const dots = Array.from(dotsWrap.querySelectorAll('[data-radial-slider-control]'));
      if (dots.length) {
        const firstDot = dots[0];
        dots.slice(1).forEach((dot) => dot.remove());
        firstDot.setAttribute('data-radial-slider-control', '1');
        firstDot.setAttribute('data-radial-slider-control-status', 'not-active');
        for (let i = 2; i <= originalItems.length; i += 1) {
          const dot = firstDot.cloneNode(true);
          dot.setAttribute('data-radial-slider-control', String(i));
          dot.setAttribute('data-radial-slider-control-status', 'not-active');
          dotsWrap.appendChild(dot);
        }
      }
    }

    const controls = Array.from(container.querySelectorAll('[data-radial-slider-control]'));
    const totalEl = container.querySelector('[data-radial-slider-total-slide]');
    const indicators = Array.from(container.querySelectorAll('[data-radial-slider-active-slide]'));

    originalItems.forEach((item, index) => {
      item.removeAttribute('data-radial-slider-item-status');
      item.removeAttribute('aria-hidden');
      item.setAttribute('role', 'group');
      item.setAttribute('aria-label', `Slide ${index + 1} of ${originalItems.length}`);
    });

    controls.forEach((btn) => {
      const value = btn.getAttribute('data-radial-slider-control');
      if (value === 'prev') btn.setAttribute('aria-label', 'Previous slide');
      if (value === 'next') btn.setAttribute('aria-label', 'Next slide');
      if (/^\d+$/.test(value)) {
        btn.setAttribute('aria-label', `Go to slide ${value}`);
        btn.setAttribute('aria-current', 'false');
      }
    });

    track.style.height = '';

    const setNumber = (el, value) => {
      if (!el) return;
      el.textContent = value < 10 ? `0${value}` : String(value);
    };

    const mod = (value, total) => ((value % total) + total) % total;

    setNumber(totalEl, originalItems.length);

    const containerStyles = getComputedStyle(container);
    const rotateStep = Math.abs(parseFloat(containerStyles.getPropertyValue('--slider-rotate'))) || 18;
    const maxLoopItems = Math.max(1, Math.floor(360 / rotateStep));

    const firstRect = originalItems[0].getBoundingClientRect();
    const itemWidth = firstRect.width;
    const itemHeight = firstRect.height;

    const originParts = getComputedStyle(originalItems[0]).transformOrigin.split(' ');
    const originY = parseFloat(originParts[1]) || itemHeight * 3.75;
    const wheelRadius = Math.max(0, originY - itemHeight / 2);

    const getBoundsAtAngle = (angle) => {
      const rad = (angle * Math.PI) / 180;
      return {
        x: Math.sin(rad) * wheelRadius,
        y: originY - Math.cos(rad) * wheelRadius,
        halfWidth: Math.abs(Math.cos(rad)) * itemWidth / 2 + Math.abs(Math.sin(rad)) * itemHeight / 2,
        halfHeight: Math.abs(Math.sin(rad)) * itemWidth / 2 + Math.abs(Math.cos(rad)) * itemHeight / 2,
      };
    };

    const isOffsetInsideContainer = (offset) => {
      const containerRect = container.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const originX = trackRect.left + trackRect.width / 2;
      const originYTop = trackRect.top;
      const leftLimit = containerRect.left - originX;
      const rightLimit = containerRect.right - originX;
      const topLimit = containerRect.top - originYTop;
      const bottomLimit = containerRect.bottom - originYTop;
      const bounds = getBoundsAtAngle(offset * rotateStep);
      const cardLeft = bounds.x - bounds.halfWidth;
      const cardRight = bounds.x + bounds.halfWidth;
      const cardTop = bounds.y - bounds.halfHeight;
      const cardBottom = bounds.y + bounds.halfHeight;
      return cardRight >= leftLimit && cardLeft <= rightLimit && cardBottom >= topLimit && cardTop <= bottomLimit;
    };

    const getVisibleOffsets = () => {
      const offsets = [0];
      const maxSide = Math.ceil(maxLoopItems / 2);
      let leftEdge = 0;
      let rightEdge = 0;

      for (let i = 1; i <= maxSide; i += 1) {
        if (!isOffsetInsideContainer(i)) break;
        offsets.push(i);
        rightEdge = i;
      }

      for (let i = 1; i <= maxSide; i += 1) {
        if (!isOffsetInsideContainer(-i)) break;
        offsets.unshift(-i);
        leftEdge = -i;
      }

      const nextLeft = leftEdge - 1;
      const nextRight = rightEdge + 1;
      if (Math.abs(nextLeft) <= maxSide) offsets.unshift(nextLeft);
      if (Math.abs(nextRight) <= maxSide) offsets.push(nextRight);
      return offsets;
    };

    const visibleOffsets = getVisibleOffsets();
    const minItemsNeeded = Math.min(maxLoopItems, Math.max(originalItems.length, visibleOffsets.length));
    const loopSets = Math.max(2, Math.ceil(minItemsNeeded / originalItems.length));
    const neededItems = loopSets * originalItems.length;
    const currentItems = Array.from(
      container.querySelectorAll('[data-radial-slider-item]:not([data-radial-slider-clone])')
    );

    for (let i = currentItems.length; i < neededItems; i += 1) {
      const clone = currentItems[i % currentItems.length].cloneNode(true);
      clone.setAttribute('data-radial-slider-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-vt-morph]').forEach((media) => {
        media.removeAttribute('data-vt-morph');
      });
      clone.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });
      track.appendChild(clone);
    }

    const items = Array.from(track.querySelectorAll(':scope > [data-radial-slider-item]'));
    const totalItems = items.length;

    track.style.height = `${itemHeight}px`;
    items.forEach((item) => {
      item.setAttribute('data-radial-slider-item-status', 'not-active');
    });

    const state = { rotation: 0 };
    container._radialSliderState = state;

    const setRotation = items.map((item) => gsap.quickSetter(item, 'rotation', 'deg'));

    const getIndexFromState = () => -state.rotation / rotateStep;

    const nearestDelta = (index, realIndex, total) => {
      const loop = Math.round((realIndex - index) / total);
      return index - (realIndex - loop * total);
    };

    let lastActiveIndex = null;

    const setIndicator = (index) => {
      const value = index + 1;
      const text = value < 10 ? `0${value}` : String(value);
      indicators.forEach((el) => {
        el.textContent = text;
      });
    };

    const updateControlStatus = (activeIndex) => {
      controls.forEach((btn) => {
        const value = btn.getAttribute('data-radial-slider-control');
        if (!/^\d+$/.test(value)) return;
        const index = Math.max(0, Math.min(originalItems.length - 1, parseInt(value, 10) - 1));
        const isActive = index === activeIndex;
        btn.setAttribute('data-radial-slider-control-status', isActive ? 'active' : 'not-active');
        btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const updateActiveUI = (activeIndex) => {
      if (activeIndex === lastActiveIndex) return;
      setIndicator(activeIndex);
      updateControlStatus(activeIndex);
      lastActiveIndex = activeIndex;
    };

    const render = () => {
      const realIndex = getIndexFromState();
      const activeIndex = mod(Math.round(realIndex), totalItems);
      const activeSlideIndex = activeIndex % originalItems.length;

      items.forEach((item, index) => {
        const rotation = nearestDelta(index, realIndex, totalItems) * rotateStep;
        item.setAttribute('data-radial-slider-item-status', index === activeIndex ? 'active' : 'inview');
        setRotation[index](rotation);
      });

      updateActiveUI(activeSlideIndex);
    };

    const marqueeEnabled = container.getAttribute('data-radial-slider-autoplay') !== 'false';
    const loopDuration = marqueeEnabled
      ? getNumberAttr(container, 'data-radial-slider-duration', getNumberAttr(container, 'data-radial-slider-autoplay-duration', DEFAULT_LOOP_DURATION))
      : 0;
    const loopDegrees = originalItems.length * rotateStep;
    const wrapRotation = gsap.utils.wrap(-loopDegrees, 0);

    container._radialSliderHoverPaused = false;
    container._radialSliderFocusPaused = false;
    let cardHoverCount = 0;

    const isCardLink = (element) =>
      element?.closest?.('[data-radial-slider-item] .radial-project-card');

    const isInteractionPaused = () =>
      container._radialSliderHoverPaused || container._radialSliderFocusPaused;

    const startMarquee = () => {
      if (prefersReducedMotion || loopDuration <= 0 || originalItems.length <= 1) return;
      if (container._radialSliderMarquee || isInteractionPaused()) return;

      container._radialSliderMarquee = gsap.to(state, {
        rotation: -loopDegrees,
        duration: loopDuration,
        ease: 'none',
        repeat: -1,
        onUpdate: render,
        modifiers: {
          rotation: (value) => wrapRotation(parseFloat(value)),
        },
      });
    };

    const stopMarquee = () => {
      if (!container._radialSliderMarquee) return;
      container._radialSliderMarquee.kill();
      container._radialSliderMarquee = null;
    };

    const pauseMarquee = () => {
      container._radialSliderMarquee?.pause();
    };

    const resumeMarquee = () => {
      if (isInteractionPaused() || !ScrollTrigger.isInViewport(container)) return;
      if (container._radialSliderMarquee) {
        container._radialSliderMarquee.resume();
        return;
      }
      startMarquee();
    };

    const syncMarqueeState = () => {
      if (isInteractionPaused()) {
        pauseMarquee();
        return;
      }

      resumeMarquee();
    };

    if (loopDuration > 0 && !prefersReducedMotion) {
      container._radialSliderScrollTrigger = ScrollTrigger.create({
        trigger: container,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: startMarquee,
        onLeave: stopMarquee,
        onEnterBack: startMarquee,
        onLeaveBack: stopMarquee,
      });

      const abort = new AbortController();
      container._radialSliderAbort = abort;
      const listenerOptions = { signal: abort.signal };

      const cardLinks = items
        .map((item) => item.querySelector('.radial-project-card'))
        .filter(Boolean);

      cardLinks.forEach((card) => {
        card.addEventListener('mouseenter', () => {
          cardHoverCount += 1;
          container._radialSliderHoverPaused = true;
          syncMarqueeState();
        }, listenerOptions);

        card.addEventListener('mouseleave', () => {
          cardHoverCount = Math.max(0, cardHoverCount - 1);
          container._radialSliderHoverPaused = cardHoverCount > 0;
          syncMarqueeState();
        }, listenerOptions);

        card.addEventListener('focusin', () => {
          container._radialSliderFocusPaused = true;
          syncMarqueeState();
        }, listenerOptions);

        card.addEventListener('focusout', (event) => {
          if (isCardLink(event.relatedTarget) && container.contains(event.relatedTarget)) return;
          container._radialSliderFocusPaused = false;
          syncMarqueeState();
        }, listenerOptions);
      });
    }

    render();
  });

  if (initRadialCardsSlider._resize) {
    window.removeEventListener('resize', initRadialCardsSlider._resize);
  }

  initRadialCardsSlider._resize = debounceOnWidthChange(initRadialCardsSlider, 200);
  window.addEventListener('resize', initRadialCardsSlider._resize);
}

function bootstrap() {
  requestAnimationFrame(() => {
    const island = document.querySelector('is-land[type="radial-cards-slider"]');
    if (island) {
      initAnimationsForElement(island);
    }
  });

  initRadialCardsSlider();
}

requestAnimationFrame(() => {
  if (document.querySelector('[data-radial-slider-init]')) {
    bootstrap();
  }
});
